import React, { useState, useEffect, lazy, Suspense } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { withTheme } from '@material-ui/core/styles';
import Icon from '@material-ui/core/Icon';
import Snackbar from '@material-ui/core/Snackbar';
import { logEvent } from '../../log/logUtil';

import './RedditPost.css';

const DialogContent = lazy(() => import('@material-ui/core/DialogContent'));
const Button = lazy(() => import('@material-ui/core/Button'));
const Dialog = lazy(() => import('@material-ui/core/Dialog'));
const DialogActions = lazy(() => import('@material-ui/core/DialogActions'));
const DialogContentText = lazy(() => import('@material-ui/core/DialogContentText'));
const RedditPostImg = lazy(() => import('../RedditPostImg/RedditPostImg'));

const PLAY_STORE_URL = 'https://bit.ly/2In2Fi7';

const RedditPost = (props) => {

    const postId = props.match.params.postId;
    const { favorites, addToFavorites, removeFromFavorites } = props;

    logEvent('screen_view', {
        firebase_screen: "Post"
    });

    const [title, setTitle] = useState('');
    const [media, setMedia] = useState(null);
    const [windowDimensions, setWindowDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const [isDialogShown, setDialogShown] = useState(false);

    const { theme } = props;
    const style = calculateMediaContainerStyle(theme, windowDimensions);

    useEffect(() => {
        const handleResponse = response => {
            const children = response.data.children;
            if (children.length !== 1) {
                return;
            }

            setTitle(children[0].data.title);
            setMedia(extractMedia(children[0].data));
        };

        const fetchData = async () => {
            try {
                const response = await (await fetch(`https://www.reddit.com/by_id/t3_${postId}.json`)).json();
                handleResponse(response);

            } catch (e) {
                // TODO handle better
                console.log(e);
            }
        };
        fetchData();
    }, [postId]);

    useEffect(() => {
        const listener = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', listener);
        return () => window.removeEventListener('resize', listener);
    }, []);

    let mediaHtmlObj;
    if (media != null) {
        if (media.type === 'image') {
            mediaHtmlObj =
                (
                    <Suspense fallback={<div>Loading...</div>}>
                        <RedditPostImg
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            post={media}
                        />
                    </Suspense>
                );
        } else if (media.type === 'video' || media.type === 'gifv') {
            mediaHtmlObj = <video src={media.url} autoPlay controls>Sorry, your browser doesn't support embedded videos.</video>
        }
    }

    const isFavorite = favorites.indexOf(postId) >= 0;

    const [snackbarMessage, setSnackbarMessage] = useState("");

    const handleClose = (event, reason) => {
        if (reason !== 'clickaway') {
            setSnackbarMessage("");
        }
    };

    return (
        <div className='container'>
            <AppBar position='static'>
                <Toolbar style={{ flexGrow: 1 }}>
                    <Typography
                        variant='h6'
                        style={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => setDialogShown(true)}>
                        {title}
                    </Typography>
                    <div onClick={() => {
                        if (isFavorite) {
                            removeFromFavorites(postId);
                            setSnackbarMessage("Removed from favorites");
                        } else {
                            addToFavorites(postId);
                            setSnackbarMessage("Added to favorites");
                        }
                    }}>
                        {isFavorite ? <Icon>favorite</Icon> : <Icon>favorite_border</Icon>}
                    </div>
                    <a
                        onClick={() => logEvent('opened_in_play_store')}
                        style={{ marginLeft: '10px' }}
                        href={PLAY_STORE_URL}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <Icon>shop</Icon>
                    </a>
                    {navigator.canShare &&
                        <Icon
                            style={{ marginLeft: '10px' }}
                            onClick={async () => {
                                try {
                                    await navigator.share({
                                        title,
                                        text: title,
                                        url: window.location.href,
                                    });
                                    logEvent('share', { content_type: 'Post' });
                                } catch (err) {
                                }
                            }}>
                            share
                        </Icon>
                    }
                </Toolbar>
            </AppBar>
            <div style={style} className='mediaContainer'>
                {mediaHtmlObj}
            </div>
            {
                isDialogShown &&
                <Suspense fallback={<div />}>
                    <MyDialog title={title} isDialogShown={isDialogShown} setDialogShown={setDialogShown} />
                </Suspense>
            }
            {
                <Suspense fallback={<div />}>
                    {createSnackbar(snackbarMessage, handleClose)}
                </Suspense>
            }
        </div>
    )
}

function MyDialog({ title, isDialogShown, setDialogShown }) {
    return (
        <Dialog open={isDialogShown} onClose={() => setDialogShown(false)}>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {title}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDialogShown(false)} color='primary'>
                    Dismiss
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function calculateMediaContainerStyle(theme, windowDimensions) {
    if (windowDimensions.width > windowDimensions.height) {
        return {
            width: '100%',
            height: `calc(100% - ${theme.overrides.MuiAppBar.root.height})`
        };
    } else {
        return {
            width: '100%',
            height: 'auto'
        };
    }
}

export default withTheme(RedditPost);

function extractMedia(obj) {
    const converters = [extractSecureMediaVideo, extractVideo, extractGifv, extractImage];
    for (const converter of converters) {
        try {
            return converter(obj);
        } catch (e) {
            // Catch so that the others converters are tried.
        }
    }

    return null;
}

function extractSecureMediaVideo(obj) {
    return {
        url: obj.crosspost_parent_list[0].secure_media.reddit_video.fallback_url,
        type: "video"
    };
}

function extractVideo(obj) {
    return {
        url: obj.media.reddit_video.fallback_url,
        type: "video"
    };
}

function extractGifv(obj) {
    return {
        url: obj.preview.reddit_video_preview.fallback_url,
        type: "gifv"
    };
}

function extractImage(obj) {
    const image = obj.preview.images[0];

    const media = [];
    const parser = new DOMParser();

    for (const resolution of image.resolutions) {
        media.push({
            url: parser.parseFromString(resolution.url, "text/html").documentElement.textContent,
            width: resolution.width,
            height: resolution.height
        });
    }

    media.push({
        url: parser.parseFromString(image.source.url, "text/html").documentElement.textContent,
        width: image.source.width,
        height: image.source.height
    });

    return {
        media,
        type: "image"
    };
}

function createSnackbar(message, onClose) {
    return (
        <Snackbar
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            open={message !== ""}
            autoHideDuration={3000}
            onClose={onClose}
            message={message}
        />
    );
}